const controllers = new WeakMap();

// Keep one gesture alive while the deferred slider implementation loads.
export function initHeroSwipe(track, options) {
    const existing = controllers.get(track);
    if (existing) {
        existing.configure(options);
        return existing;
    }

    const pointerScope = track.ownerDocument;
    let delegate = options;
    let gesture;
    let pendingDirection;
    let suppressClick = false;

    function removePointerListeners() {
        pointerScope.removeEventListener('pointermove', movePointer);
        pointerScope.removeEventListener('pointerup', finishPointer);
        pointerScope.removeEventListener('pointercancel', cancelPointer);
        pointerScope.removeEventListener('touchmove', preventTouchScroll);
    }

    function releaseCapture(pointerId) {
        if (!track.hasPointerCapture?.(pointerId)) return;
        try {
            track.releasePointerCapture(pointerId);
        } catch (error) {
            if (error.name !== 'NotFoundError') throw error;
        }
    }

    function finishGesture(event, cancelled = false) {
        if (!gesture || event.pointerId !== gesture.pointerId) return;
        const completed = gesture;
        gesture = undefined;
        removePointerListeners();
        releaseCapture(event.pointerId);
        completed.root.classList.remove('is-drag-primed', 'is-dragging');
        if (cancelled || !completed.horizontal || !track.isConnected) return;

        const distance = event.clientX - completed.startX;
        const elapsed = Math.max(1, performance.now() - completed.startedAt);
        const threshold = Math.min(64, Math.max(26, completed.width * 0.1));
        if (Math.abs(distance) < threshold && Math.abs(distance / elapsed) < 0.45) return;

        suppressClick = true;
        const direction = distance < 0 ? 'next' : 'previous';
        if (delegate.move) void delegate.move(direction);
        else pendingDirection = direction;
        window.setTimeout(() => {
            suppressClick = false;
        });
    }

    function movePointer(event) {
        if (!gesture || event.pointerId !== gesture.pointerId) return;
        if (!track.isConnected) {
            finishGesture(event, true);
            return;
        }
        const distanceX = event.clientX - gesture.startX;
        const distanceY = event.clientY - gesture.startY;
        if (!gesture.horizontal) {
            const horizontalDistance = Math.abs(distanceX);
            const verticalDistance = Math.abs(distanceY);
            if (Math.max(horizontalDistance, verticalDistance) < 10) return;
            if (horizontalDistance < verticalDistance * 1.4) {
                finishGesture(event, true);
                return;
            }
            gesture.horizontal = true;
            try {
                track.setPointerCapture?.(event.pointerId);
            } catch {
                // Document listeners also work when an editor frame cannot capture.
            }
            gesture.root.classList.add('is-drag-primed', 'is-dragging');
        }
        event.preventDefault();
    }

    function finishPointer(event) {
        finishGesture(event);
    }

    function preventTouchScroll(event) {
        // Cancelling a PointerEvent alone leaves Chrome's native touch/fling
        // recognizer active, which can consume the next tap on an arrow.
        // Only cancel an accepted single-finger horizontal swipe; vertical
        // scrolling and multi-touch zoom remain native.
        if (gesture?.horizontal && event.touches.length === 1) event.preventDefault();
    }

    function cancelPointer(event) {
        finishGesture(event, true);
    }

    function startPointer(event) {
        if (
            gesture ||
            pendingDirection ||
            !track.isConnected ||
            !event.isPrimary ||
            event.button !== 0 ||
            delegate.canStart?.() === false
        )
            return;
        const root = track.closest('[data-blaze-shell], .z-blaze-slider') || track;
        gesture = {
            root,
            horizontal: false,
            pointerId: event.pointerId,
            startedAt: performance.now(),
            startX: event.clientX,
            startY: event.clientY,
            width: root.clientWidth,
        };
        pointerScope.addEventListener('pointermove', movePointer, { passive: false });
        pointerScope.addEventListener('pointerup', finishPointer);
        pointerScope.addEventListener('pointercancel', cancelPointer);
        pointerScope.addEventListener('touchmove', preventTouchScroll, { passive: false });
        if (!delegate.move && delegate.initialize) {
            void delegate.initialize().then(() => {
                if (!delegate.move) controller.fallback();
            });
        }
    }

    function click(event) {
        if (!suppressClick) return;
        suppressClick = false;
        event.preventDefault();
        event.stopImmediatePropagation();
    }

    function preventNativeDrag(event) {
        event.preventDefault();
    }

    const controller = {
        configure(next) {
            delegate = { ...delegate, ...next };
            if (!delegate.move || !pendingDirection) return;
            const direction = pendingDirection;
            pendingDirection = undefined;
            if (track.isConnected) void delegate.move(direction);
        },
        fallback() {
            removePointerListeners();
            if (gesture) releaseCapture(gesture.pointerId);
            gesture?.root.classList.remove('is-drag-primed', 'is-dragging');
            gesture = undefined;
            pendingDirection = undefined;
            track.removeEventListener('pointerdown', startPointer);
            track.removeEventListener('click', click, true);
            track.removeEventListener('dragstart', preventNativeDrag);
            track.removeAttribute('data-blaze-swipe-ready');
            controllers.delete(track);
        },
    };
    controllers.set(track, controller);
    // Set touch-action before pointerdown: changing it mid-gesture is too late.
    track.setAttribute('data-blaze-swipe-ready', '');
    track.addEventListener('pointerdown', startPointer, { passive: true });
    track.addEventListener('click', click, true);
    track.addEventListener('dragstart', preventNativeDrag);
    return controller;
}
